import {
  IconBell,
  IconBuilding,
  IconBuildingCommunity,
  IconCalendarEvent,
  IconChecklist,
  IconCoins,
  IconDatabase,
  IconFileImport,
  IconHash,
  IconListCheck,
  IconPackage,
  IconRuler,
  IconSettings,
  IconSitemap,
  IconTruck,
  IconUsers,
  IconArrowsExchange,
  IconCurrencyDollar,
  IconCalculator,
} from '@tabler/icons-react';
import { defineModule } from '../types';
import { indexRoute, lazyRoute, routeGroup } from '../routing';
import { P } from './perms';
import { lazy } from 'react';

const widgets = () => import('./widgets');
const MyApprovalsWidget = lazy(() => widgets().then((m) => ({ default: m.MyApprovalsWidget })));
const UnreadNotificationsWidget = lazy(() => widgets().then((m) => ({ default: m.UnreadNotificationsWidget })));

const simple = () => import('./pages/SimplePages');
const items = () => import('./pages/ItemsPages');
const customers = () => import('./pages/CustomersPages');
const suppliers = () => import('./pages/SuppliersPages');

/** Personal routes of every signed-in user (inbox, notifications, profile). */
const personalRoutes = [
  lazyRoute('approvals', () => import('./pages/MyApprovalsPage'), { crumb: 'core:nav.myApprovals' }),
  lazyRoute('notifications', () => import('./pages/NotificationsPage'), { crumb: 'common:notifications.title' }),
  lazyRoute('profile', () => import('./pages/ProfilePage'), { crumb: 'common:user.profile' }),
];

/**
 * CORE — company, organisation, master data (customers, services & products, suppliers),
 * approvals, settings, imports (FR-CORE-01..12).
 */
export default defineModule({
  code: 'core',
  nameAr: 'البيانات الأساسية',
  nameEn: 'Core & master data',
  icon: IconDatabase,
  order: 10,
  menu: [
    {
      key: 'core.org',
      labelKey: 'core:nav.organisation',
      icon: IconBuilding,
      children: [
        { key: 'core.company', labelKey: 'core:nav.company', path: '/core/company', icon: IconBuildingCommunity, permission: P.company.read },
        { key: 'core.departments', labelKey: 'core:nav.departments', path: '/core/departments', icon: IconSitemap, permission: P.department.read },
        { key: 'core.costCenters', labelKey: 'core:nav.costCenters', path: '/core/cost-centers', icon: IconCalculator, permission: P.costCenter.read },
      ],
    },
    {
      key: 'core.master',
      labelKey: 'core:nav.masterData',
      icon: IconPackage,
      children: [
        { key: 'core.customers', labelKey: 'core:nav.customers', path: '/core/customers', icon: IconUsers, permission: P.customer.read },
        { key: 'core.items', labelKey: 'core:nav.items', path: '/core/items', icon: IconPackage, permission: P.item.read },
        { key: 'core.suppliers', labelKey: 'core:nav.suppliers', path: '/core/suppliers', icon: IconTruck, permission: P.supplier.read },
        { key: 'core.uoms', labelKey: 'core:nav.uoms', path: '/core/uoms', icon: IconRuler, permission: P.uom.read },
      ],
    },
    {
      key: 'core.finance',
      labelKey: 'core:nav.financeSetup',
      icon: IconCoins,
      children: [
        { key: 'core.currencies', labelKey: 'core:nav.currencies', path: '/core/currencies', icon: IconCurrencyDollar, permission: P.currency.read },
        { key: 'core.fxRates', labelKey: 'core:nav.fxRates', path: '/core/fx-rates', icon: IconArrowsExchange, permission: P.fxRate.read },
        { key: 'core.holidays', labelKey: 'core:nav.holidays', path: '/core/holidays', icon: IconCalendarEvent, permission: P.holiday.read },
        { key: 'core.numberSeries', labelKey: 'core:nav.numberSeries', path: '/core/number-series', icon: IconHash, permission: P.numberSeries.read },
      ],
    },
    {
      key: 'core.workflow',
      labelKey: 'core:nav.workflow',
      icon: IconChecklist,
      children: [
        { key: 'core.myApprovals', labelKey: 'core:nav.myApprovals', path: '/approvals', icon: IconChecklist },
        { key: 'core.approvalRules', labelKey: 'core:nav.approvalRules', path: '/core/approval-rules', icon: IconListCheck, permission: P.approvalRule.configure },
        { key: 'core.notifications', labelKey: 'common:notifications.title', path: '/notifications', icon: IconBell },
      ],
    },
    {
      key: 'core.system',
      labelKey: 'core:nav.system',
      icon: IconSettings,
      children: [
        { key: 'core.settings', labelKey: 'core:nav.settings', path: '/core/settings', icon: IconSettings, permission: P.setting.read },
        { key: 'core.imports', labelKey: 'core:nav.imports', path: '/core/imports', icon: IconFileImport, permission: P.import.run },
      ],
    },
  ],
  routes: {
    admin: [
      lazyRoute('core/company', () => import('./pages/CompanyPage'), { permission: P.company.read, crumb: 'core:nav.company' }),
      lazyRoute('core/departments', () => import('./pages/DepartmentsPage'), { permission: P.department.read, crumb: 'core:nav.departments' }),
      lazyRoute('core/cost-centers', () => simple().then((m) => ({ default: m.CostCentersPage })), { permission: P.costCenter.read, crumb: 'core:nav.costCenters' }),
      lazyRoute('core/uoms', () => import('./pages/UomsPage'), { permission: P.uom.read, crumb: 'core:nav.uoms' }),
      routeGroup('core/items', 'core:nav.items', [
        indexRoute(() => items().then((m) => ({ default: m.ItemsPage })), P.item.read),
        lazyRoute(':id', () => items().then((m) => ({ default: m.ItemDetailPage })), { permission: P.item.read, crumb: 'core:items.detail' }),
      ]),
      routeGroup('core/customers', 'core:nav.customers', [
        indexRoute(() => customers().then((m) => ({ default: m.CustomersPage })), P.customer.read),
        lazyRoute(':id', () => customers().then((m) => ({ default: m.CustomerDetailPage })), { permission: P.customer.read, crumb: 'core:customers.detail' }),
      ]),
      routeGroup('core/suppliers', 'core:nav.suppliers', [
        indexRoute(() => suppliers().then((m) => ({ default: m.SuppliersPage })), P.supplier.read),
        lazyRoute(':id', () => suppliers().then((m) => ({ default: m.SupplierDetailPage })), { permission: P.supplier.read, crumb: 'core:suppliers.detail' }),
      ]),
      lazyRoute('core/currencies', () => simple().then((m) => ({ default: m.CurrenciesPage })), { permission: P.currency.read, crumb: 'core:nav.currencies' }),
      lazyRoute('core/fx-rates', () => import('./pages/FxRatesPage'), { permission: P.fxRate.read, crumb: 'core:nav.fxRates' }),
      lazyRoute('core/holidays', () => simple().then((m) => ({ default: m.HolidaysPage })), { permission: P.holiday.read, crumb: 'core:nav.holidays' }),
      lazyRoute('core/number-series', () => simple().then((m) => ({ default: m.NumberSeriesPage })), { permission: P.numberSeries.read, crumb: 'core:nav.numberSeries' }),
      lazyRoute('core/approval-rules', () => import('./pages/ApprovalRulesPage'), { permission: P.approvalRule.configure, crumb: 'core:nav.approvalRules' }),
      lazyRoute('core/settings', () => import('./pages/SettingsPage'), { permission: P.setting.read, crumb: 'core:nav.settings' }),
      lazyRoute('core/imports', () => import('./pages/ImportsPage'), { permission: P.import.run, crumb: 'core:nav.imports' }),
      ...personalRoutes,
    ],
  },
  dashboards: [
    { key: 'core.approvals', titleKey: 'core:widgets.myApprovals', component: MyApprovalsWidget, order: 1, span: { base: 12, md: 6, lg: 4 } },
    { key: 'core.notifications', titleKey: 'core:widgets.notifications', component: UnreadNotificationsWidget, order: 2, span: { base: 12, md: 6, lg: 4 } },
  ],
});
